import React, {ReactNode, useRef, useMemo, CSSProperties} from 'react';
import {r} from "@/utils/responsive";
import {MaskShape, useMaskShapes} from "@/hooks/useMaskShapes";
import SvgEffects, {PRESET_SVG_FILTERS} from
        '@/components/SVGEffects';
import DisabledWrapper from "@/components/DisabledWrapper";

interface ButtonReforgeProps extends React.HTMLAttributes<HTMLButtonElement> {
    children?: ReactNode;
    width?: number;
    height?: number;
    minHeight?: number;
    disabled?: boolean;
    flattenMask?: boolean;
    strokeWidth?: number;
    enableGlowEffect?: boolean;
}

const buttonMaskShapes: MaskShape[] = [
    {
        width: 110,
        height: 110,
        path: "M110 110H55C33.4801 110 22.72 110 14.8799 105.035C10.8825 102.504 7.4962 99.1175 4.96484 95.1201C0.000157833 87.28 0 76.5199 0 55C0 33.4801 0.000157833 22.72 4.96484 14.8799C7.4962 10.8825 10.8825 7.4962 14.8799 4.96484C22.72 0.000157833 33.4801 0 55 0H110V110Z",
        position: "left",
    },
    {
        fluid: true,
        position: 'middle'
    },
    {
        width: 220,
        height: 110,
        path: "M170 0C186.79 0 195.186 -1.57356e-05 201.682 3.07227C208.374 6.23761 213.762 11.6258 216.928 18.3184C220 24.8143 220 33.2095 220 50C220 66.7905 220 75.1857 216.928 81.6816C213.762 88.3742 208.374 93.7624 201.682 96.9277C195.186 100 186.79 100 170 100H136.777C130.856 100 127.896 100 124.954 100.291C122.013 100.582 119.109 101.16 113.303 102.318L86.4189 107.682C80.6121 108.84 77.7081 109.419 74.7666 109.71C71.8252 110 68.8644 110 62.9434 110H0V0H170Z",
        position: "right",
    }
]

const ButtonReforge: React.FC<ButtonReforgeProps> = ({
                                                         className,
                                                         children,
                                                         width,
                                                         height,
                                                         minHeight = 121,
                                                         style,
                                                         disabled,
                                                         flattenMask = true,
                                                         strokeWidth = 23,
                                                         enableGlowEffect = true,
                                                         ...props
                                                     }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Создаем уникальный ID для маски
    const maskId = useMemo(() => `button-mask-${Math.random().toString(36).substring(2, 11)}`, []);

    // Получаем SVG элемент с масками используя svg.js
    const {
        svgJSX,
        clipPath,
        flattenedSVG,
        strokeSVG,
        containerWidth,
    } = useMaskShapes(buttonMaskShapes, maskId, buttonRef, flattenMask, strokeWidth);

    const buttonStyle: CSSProperties = {
        ...(width ? {width: r.vmax(width)} : {width: '100%'}),
        ...(height && {height: r.vmax(height)}),
        minHeight: r.vmax(minHeight),
        background: '#5282FF',
        clipPath: clipPath,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: r.vmax(12),
        position: 'relative',
        zIndex: 1,
        ...style
    };

    return (
        <div style={{position: 'relative', display: 'inline-block', width: '100%'}}>
            {/* SVG с определением сложного фильтра */}
            {enableGlowEffect && (
                <svg width="0" height="0" style={{position: 'absolute'}}>
                    <defs>
                        <filter
                            id={`glow-filter-${maskId}`}
                            x="-50%"
                            y="-50%"
                            width="200%"
                            height="300%"
                            filterUnits="userSpaceOnUse"
                            colorInterpolationFilters="sRGB"
                        >
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feOffset dy="10"/>
                            <feGaussianBlur stdDeviation="20"/>
                            <feComposite in2="hardAlpha" operator="out"/>
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0.333333 0 0 0 0 0.533333 0 0 0 0 1 0 0 0 1 0"
                            />
                            <feBlend
                                mode="plus-lighter"
                                in2="BackgroundImageFix"
                                result="effect1_dropShadow"
                            />
                            <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="effect1_dropShadow"
                                result="shape"
                            />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feMorphology
                                radius="8.33"
                                operator="dilate"
                                in="SourceAlpha"
                                result="effect2_innerShadow"
                            />
                            <feOffset dy="-24"/>
                            <feGaussianBlur stdDeviation="10"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0.089 0 0 0 0 0.65 0 0 0 0 0.8 0 0 0 1 0"
                            />
                            <feBlend
                                mode="normal"
                                in2="shape"
                                result="effect2_innerShadow"
                            />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feMorphology
                                radius="32"
                                operator="dilate"
                                in="SourceAlpha"
                                result="effect3_innerShadow"
                            />
                            <feOffset dy="-26"/>
                            <feGaussianBlur stdDeviation="29"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0.322 0 0 0 0 0.51 0 0 0 0 1 0 0 0 1 0"
                            />
                            <feBlend
                                mode="color-dodge"
                                in2="effect2_innerShadow"
                                result="effect3_innerShadow"
                            />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feMorphology
                                radius="4.76"
                                operator="dilate"
                                in="SourceAlpha"
                                result="effect4_innerShadow"
                            />
                            <feOffset dy="5"/>
                            <feGaussianBlur stdDeviation="25"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0"
                            />
                            <feBlend
                                mode="plus-lighter"
                                in2="effect3_innerShadow"
                                result="effect4_innerShadow"
                            />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feOffset dy="-26"/>
                            <feGaussianBlur stdDeviation="17"/>
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.65 0"
                            />
                            <feBlend
                                mode="plus-lighter"
                                in2="effect4_innerShadow"
                                result="effect5_innerShadow"
                            />
                        </filter>
                    </defs>
                </svg>
            )}

            {svgJSX}
            <button
                {...props}
                ref={buttonRef}
                style={{
                    ...buttonStyle,
                    filter: enableGlowEffect ? `url(#glow-filter-${maskId})` : undefined
                }}
            >
                {children}
            </button>
            {flattenedSVG && (
                <DisabledWrapper style={{
                    color: enableGlowEffect ? `#5588FF` : 'transparent',
                    transform: `translateY(${r.vmax(10)})`,
                    overflow: 'visible',
                    filter: `blur(${r.vmax(40)})`,
                }} dangerouslySetInnerHTML={{__html: flattenedSVG}}>
                </DisabledWrapper>
            )}
        </div>
    );
};

export default ButtonReforge;
